// Ачивки в интерфейсе: раздел профиля (полученные, по тапу — все с условиями), ачивки партии на экране итога и
// новая ачивка — всплывающей карточкой (FX.achievements) и в ящик 🔔. Полученные в этой сессии вкладки (sessionStorage)
// в своём профиле мигают и стоят первыми. Условия и названия — src/engine/achievements.ts; что получено — с сервера
// (профиль: GET api/player, итог партии: GET api/rating-change).
import { h, Fragment } from 'preact';
import type { AchStat, PlayerProfile, RatingChange } from '../types.ts';
import { ACH_SERIES, ACH_SINGLES, ACH_INFO, tierId, winTiers, cumulativeTiers } from '../engine/achievements.ts';
import type { AchSeries } from '../engine/achievements.ts';
import { Btn, Hint, cx } from './ui-kit.ts';
import type { V } from './ui-kit.ts';
import * as FX from './fx.ts';

// ---------- полученные в этой сессии ----------

const RECENT_KEY = 'krutagidon-ach-recent';

/** id ачивок (ступеней), полученных в этой сессии вкладки */
export function recentAch(): Set<string> {
  try {
    const raw = sessionStorage.getItem(RECENT_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function markRecent(ids: readonly string[]): void {
  try {
    sessionStorage.setItem(RECENT_KEY, JSON.stringify([...new Set([...recentAch(), ...ids])]));
  } catch {
    /* приватный режим — без подсветки */
  }
}

/** Карточка ачивки в профиле: серия — её высшая полученная ступень (и следующая цель), разовая — сама ачивка */
export interface AchEntry {
  key: string;
  icon: string;
  name: string;
  text: string;
  earned: boolean;
  /** серия: получено ступеней из скольких */
  tier?: number;
  tiers?: number;
  /** условие следующей ступени */
  next?: string;
  /** сколько партий принесли (высшую ступень) */
  count?: number;
  /** получена в этой сессии (высшая ступень серии) — мигает */
  fresh?: boolean;
}

/** Всего ачивок (каждая ступень серии — отдельная) */
export const ACH_TOTAL = ACH_INFO.size;

/**
 * Карточки ачивок игрока: полученные в этой сессии (recent) — первыми, затем остальные полученные (в порядке списка),
 * затем — ещё не полученные
 */
export function achEntries(p: Pick<PlayerProfile, 'wins' | 'achievements'> & Partial<Pick<PlayerProfile, 'bosses'>>, recent: ReadonlySet<string> = new Set()): { list: AchEntry[]; got: number } {
  const stats = new Map<string, AchStat>((p.achievements ?? []).map((a) => [a.id, a]));
  const beaten = (p.bosses ?? []).filter((b) => b.wins > 0).length;
  const cumul = new Set([...winTiers(p.wins), ...cumulativeTiers('bosses', beaten)]);
  const has = (id: string): boolean => stats.has(id) || cumul.has(id);
  let got = 0;
  const series = ACH_SERIES.map((s: AchSeries): AchEntry => {
    let tier = 0;
    s.tiers.forEach((_, i) => { if (has(tierId(s, i))) tier = i + 1; });
    got += tier;
    const at = s.tiers[Math.max(0, tier - 1)]!;
    const next = s.tiers[tier];
    return {
      key: s.id, icon: s.icon, name: at.name, text: s.text(at.n), earned: tier > 0, tier, tiers: s.tiers.length,
      ...(tier > 0 && next ? { next: s.text(next.n) } : {}),
      ...(tier > 0 && stats.get(tierId(s, tier - 1)) ? { count: stats.get(tierId(s, tier - 1))!.count } : {}),
      ...(tier > 0 && recent.has(tierId(s, tier - 1)) ? { fresh: true } : {}),
    };
  });
  const singles = ACH_SINGLES.map((a): AchEntry => {
    const st = stats.get(a.id);
    if (st) got++;
    return { key: a.id, icon: a.icon, name: a.name, text: a.text, earned: !!st, ...(st ? { count: st.count } : {}), ...(st && recent.has(a.id) ? { fresh: true } : {}) };
  });
  const all = [...series, ...singles];
  return { list: [...all.filter((e) => e.fresh), ...all.filter((e) => e.earned && !e.fresh), ...all.filter((e) => !e.earned)], got };
}

/** Ступени серии точками: ●●○ */
const pips = (tier: number, of: number): string => '●'.repeat(tier) + '○'.repeat(Math.max(0, of - tier));

export function AchCardV({ e }: { e: AchEntry }): V {
  return h('div', { class: cx('ach', !e.earned && 'locked', e.fresh && 'fresh'), 'data-ach': e.key },
    h('span', { class: 'ach-ic' }, e.icon),
    h('div', { class: 'ach-main' },
      h('div', { class: 'ach-name' }, h('b', null, e.name), e.count && e.count > 1 ? h('span', { class: 'ach-n' }, `×${e.count}`) : null,
        e.fresh ? h('span', { class: 'ach-new' }, 'новая!') : null),
      e.tiers && e.tiers > 1 ? h('div', { class: 'ach-pips', title: `ступень ${e.tier} из ${e.tiers}` }, pips(e.tier ?? 0, e.tiers)) : null,
      h('div', { class: 'ach-text' }, e.text),
      e.next ? h('div', { class: 'ach-next' }, `Дальше: ${e.next}`) : null));
}

/** Раздел «Ачивки» профиля: полученные; all — и остальные (с условиями), переключатель — ссылкой */
export function AchSectionV({ p, owner, all }: { p: PlayerProfile; owner: 'me' | 'other'; all: boolean }): V {
  // мигают только свои: сессия знает, что получил этот игрок, а не тот, чей профиль открыт
  const { list, got } = achEntries(p, owner === 'me' ? recentAch() : undefined);
  const shown = all ? list : list.filter((e) => e.earned);
  return h(Fragment, null,
    h('h3', { id: 'ach-head' }, `Ачивки · ${got} из ${ACH_TOTAL}`),
    shown.length
      ? h('div', { class: 'ach-grid' }, shown.map((e) => h(AchCardV, { e })))
      : h(Hint, null, owner === 'me' ? 'Пока ни одной — они даются за яркие ходы и необычные победы в партиях рейтинга.' : 'Пока ни одной.'),
    h(Btn, { cls: 'link', id: 'btn-ach-all', c: { k: 'achAll', owner } }, all ? 'Только полученные' : 'Все ачивки и как их получить'));
}

/** Ачивка партии на экране итога */
export interface GameAchItem {
  id: string;
  icon: string;
  name: string;
  text: string;
  /** у игрока впервые */
  first: boolean;
  /** ступени серии точками (●●○) — у ступеней серий */
  pips?: string;
}

/** Ачивки партии: с сервера + новая ступень «Побед», если этой победой она взята */
export function gameAchItems(rc: RatingChange): GameAchItem[] {
  const out: { id: string; first: boolean }[] = [];
  if (rc.won && rc.wins) {
    const before = new Set(winTiers(rc.wins - 1));
    for (const id of winTiers(rc.wins)) if (!before.has(id)) out.push({ id, first: true });
  }
  if (rc.won && rc.newBoss && rc.bossesBeaten) {
    const before = new Set(cumulativeTiers('bosses', rc.bossesBeaten - 1));
    for (const id of cumulativeTiers('bosses', rc.bossesBeaten)) if (!before.has(id)) out.push({ id, first: true });
  }
  for (const a of rc.achievements ?? []) out.push({ id: a.id, first: a.first });
  // от серии — только высшая ступень партии (ступени ниже пришли вместе с ней)
  const top = new Map<string, number>();
  for (const a of out) {
    const i = ACH_INFO.get(a.id);
    if (i?.series && (i.tier ?? 0) > (top.get(i.series.id) ?? 0)) top.set(i.series.id, i.tier ?? 0);
  }
  return out.filter((a) => { const i = ACH_INFO.get(a.id); return !i?.series || i.tier === top.get(i.series.id); }).flatMap((a) => {
    const info = ACH_INFO.get(a.id);
    return info ? [{ id: a.id, icon: info.icon, name: info.name, text: info.text, first: a.first, ...(info.series ? { pips: pips(info.tier ?? 0, info.series.tiers.length) } : {}) }] : [];
  });
}

/** Ачивки партии в плашке рейтинга на экране итога: новые — с пометкой */
export function GameAchV({ items }: { items: readonly GameAchItem[] }): V {
  if (!items.length) return null;
  return h('div', { class: 'rc-ach' },
    h('div', { class: 'rc-ach-head' }, `🏅 Ачивки за партию: ${items.length}`),
    items.map((a, i) => h('div', { class: cx('rc-ach-item', a.first && 'first'), 'data-ach': a.id, style: `--i: ${i}` },
      h('span', { class: 'ach-ic' }, a.icon),
      h('div', { class: 'ach-main' },
        h('div', { class: 'ach-name' }, h('b', null, a.name), a.first ? h('span', { class: 'ach-new' }, 'новая!') : null),
        h('div', { class: 'ach-text' }, a.text)))));
}

/**
 * Новые ачивки партии (впервые у игрока): запомнить для подсветки в профиле и показать всплывающими карточками.
 * Возвращает, были ли новые.
 */
export function celebrate(items: readonly GameAchItem[]): boolean {
  const fresh = items.filter((a) => a.first);
  if (!fresh.length) return false;
  markRecent(fresh.map((a) => a.id));
  FX.achievements(fresh.map((a) => ({ icon: a.icon, name: a.name, text: a.text, ...(a.pips ? { pips: a.pips } : {}) })));
  return true;
}
